// @ts-strict-ignore
import React from "react";
import { Box, ContentBox, CopyContainer } from "./CoinPosts.styled";
import { Heading } from "app/_components/components/Text/Heading";
import { Body } from "app/_components/components/Text/Body";
import { ContentfulResponsiveImage } from "components/common/image/ContentfulResponsiveImage";
import type { CoinPostsProps } from "types/prices";
import type { BlogPostType, BlogContentType } from "types/blog-type";
import readingTime from "reading-time";
import type { CryptopediaArticleType } from "types/cryptopedia";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import NextLink from "components/parts/NextLink";
import { URLS } from "constants/common";
import { FILES } from "i18n/constants";
import { useTranslations } from "i18n";

const CoinPosts = (props: CoinPostsProps) => {
  const { data, articles, title, coinSlug } = props?.coinPosts || {};
  const isPosts = data && data.length > 0;
  const isArticles = articles && articles.length > 0;
  const t = useTranslations(FILES.PRICES);

  const renderTitle = ({ title }: { title: string }) => {
    return (
      <React.Fragment>
        {title && (
          <Heading
            size="md"
            className="mb-4 !leading-[28px] !font-medium !tracking-[-0.5px]"
          >
            {title}
          </Heading>
        )}
      </React.Fragment>
    );
  };

  const renderContent = ({
    title,
    slug,
    image,
    name,
    readTime
  }: BlogContentType) => {
    return (
      <NextLink href={slug || "#"} id={`register-blog-${title}`}>
        <Box>
          {image?.url && (
            <ContentfulResponsiveImage
              priority
              alt={image?.altText}
              sizes={[
                {
                  media: "(min-width: 100px)",
                  src: image?.url,
                  height: 80,
                  width: 144
                }
              ]}
            />
          )}
          <CopyContainer>
            <Body size="sm" className="m-0">
              {title}
            </Body>
            <Body size="sm" className="m-0 text-gray-600">
              {name} • {readTime?.text}
            </Body>
          </CopyContainer>
        </Box>
      </NextLink>
    );
  };

  const renderSeeMore = ({ link }: { link: string }) => {
    return (
      <NextLink href={link || "#"} id="see-more">
        <Body size="sm" className="m-0 mt-4 inline-block underline">
          {t("seeMore")}
        </Body>
      </NextLink>
    );
  };

  return (
    <React.Fragment>
      {isPosts && (
        <ContentBox>
          {renderTitle({ title })}
          {isPosts &&
            data.map((item: BlogPostType, index: number) => {
              const readTime = readingTime(item?.blogPostContent);
              const title = item?.title || "";
              const slug = item?.slug ? `/blog/${item?.slug}` : "";
              const name =
                item?.blogAuthors &&
                item?.blogAuthors.length > 0 &&
                item?.blogAuthors[0]?.authorName;
              const image = {
                url: item?.blogHeroImage?.url || "",
                altText: item?.blogHeroImage?.alt || ""
              };
              return (
                <React.Fragment key={index}>
                  {renderContent({ title, slug, name, image, readTime })}
                </React.Fragment>
              );
            })}
          {renderSeeMore({ link: URLS.COIN_BLOG.replace("{COIN}", coinSlug) })}
        </ContentBox>
      )}
      {isArticles && (
        <ContentBox>
          {renderTitle({ title })}
          {isArticles &&
            articles.map((item: CryptopediaArticleType, index: number) => {
              const readTime = readingTime(
                documentToPlainTextString(item?.body || null)
              );
              const title = item?.title || "";
              const slug = item?.slug ? `/cryptopedia/${item?.slug}` : "";
              const image = {
                url: item?.featuredImage?.url || "",
                altText: item?.featuredImage?.title || ""
              };
              const name =
                item?.authors &&
                item?.authors.length > 0 &&
                item?.authors[0]?.name;
              return (
                <React.Fragment key={index}>
                  {renderContent({ title, slug, name, image, readTime })}
                </React.Fragment>
              );
            })}
          {renderSeeMore({
            link: URLS.COIN_CRYPTOPEDIA.replace("{COIN}", coinSlug)
          })}
        </ContentBox>
      )}
    </React.Fragment>
  );
};

export default CoinPosts;
